import { useRef, useState } from 'react';
import { handleDownloadEditedImage } from '../components/EditImageTool';
import { getRenderOriginalFile, getRenderOtherFile, wait } from '../helpers/jsHelper';
import { uploadEditedProductImage } from '../helpers/productImageHelper';
import { applyCopiedEdits } from '../helpers/imageEditsHelper';

// Give the replaced key a moment to be servable before reading the row back.
const REFETCH_AFTER_UPLOAD_DELAY = 2000;

/**
 * The Post Edit previews and the "bake the edits into the stored file" step,
 * shared by the Silo and Lifestyle sections of Product Page V1.
 *
 * Saving edits only stores `edit_config` against the render, so the product
 * image's own `image_file` and `thumbnail` keep the picture as it was produced.
 * Every route that saves edits - the editor's Apply and Paste & Apply alike -
 * has to write the edited picture into them too, or the card carries on showing
 * the unedited version.
 */
const useProductImageEdits = ({ productId, setImageList, fetchRenderData }) => {
  const [editedImages, setEditedImages] = useState({});
  // A count rather than a flag: a paste renders and then uploads, and the
  // render's own loader must not switch the spinner off mid-upload.
  const [pendingCount, setPendingCount] = useState(0);
  // A paste saves, renders and uploads, so it stays in flight long enough for
  // more clicks to land. Guarding per image id rather than globally: a second
  // click on the SAME card is a double fire and is dropped, but pasting onto
  // another card meanwhile is a real thing to want.
  const pastingIdsRef = useRef(new Set());
  // The images a Paste & Apply is running against. Drives the badge on each of
  // their cards: a paste started from a card has no lightbox to show a loader
  // in, so without it the several seconds of work look like nothing happening.
  const [pastingImages, setPastingImages] = useState([]);

  const withLoader = async (run) => {
    setPendingCount((count) => count + 1);
    try {
      return await run();
    } finally {
      setPendingCount((count) => count - 1);
    }
  };

  /** Drops a cached Post Edit preview so the next view regenerates it. */
  const invalidateEditedImage = (imageId) =>
    setEditedImages((prev) => {
      if (!imageId || !(imageId in prev)) return prev;
      const next = { ...prev };
      delete next[imageId];
      return next;
    });

  const hasSavedEdits = (image) =>
    !!Object.keys(image?.render_image_details?.edit_config || {}).length;

  /** Renders the Post Edit blob for `image` from its saved edit_config. */
  const renderEditedImage = async (image) => {
    const editConfig = image?.render_image_details?.edit_config;
    if (!image || !hasSavedEdits(image)) return null;

    return withLoader(async () => {
      try {
        const editedImageUrl = await handleDownloadEditedImage({
          // The edits are applied ON TOP OF the render as produced, never the
          // Post Edit preview, or the effects would compound each time.
          url: getRenderOriginalFile(image),
          imageToolData: editConfig,
          otherImage: getRenderOtherFile(image),
          colorPassImage: image?.render_image_details?.color_pass_file,
          title: image?.name,
          returnFile: true,
        });

        if (editedImageUrl) {
          setEditedImages((prev) => ({ ...prev, [image.id]: editedImageUrl }));
        }
        return editedImageUrl;
      } catch (error) {
        console.error('Error generating edited image preview:', error);
        return null;
      }
    });
  };

  /**
   * The Post Edit blob for `image`, serving the cached one when there is one.
   * Returns the blob URL, so a caller that needs it immediately does not have
   * to wait for the state update.
   */
  const ensureEditedImage = async (image) =>
    (hasSavedEdits(image) && editedImages?.[image.id]) || renderEditedImage(image);

  /**
   * Everything that follows an image's edits being saved: keep the row and the
   * Post Edit preview in step, write the edited picture into the stored file
   * and thumbnail, then refetch to pick the new row up.
   *
   * Awaited by callers so they can hold their loader until the image is really
   * stored - saying "saved" over an in-flight multi-MB upload read as a lie.
   */
  const storeEditedImage = async ({ image, editedImageUrl }) => {
    setImageList((prevList) => prevList.map((img) => (img.id === image?.id ? image : img)));

    if (editedImageUrl) {
      setEditedImages((prev) => ({ ...prev, [image.id]: editedImageUrl }));
    } else {
      // The eager regeneration failed, so the preview kept from the previous
      // edit_config has to go rather than be served as this one.
      invalidateEditedImage(image?.id);
    }

    try {
      const uploaded = await uploadEditedProductImage({ image, productId, editedImageUrl });
      if (!uploaded) return;
      // Not awaited on purpose - the caller waits for the upload, not for the
      // list to catch up.
      wait(REFETCH_AFTER_UPLOAD_DELAY).then(() => fetchRenderData({ noCache: true }));
    } catch (error) {
      console.error('Failed to save the edited image:', error);
    }
  };

  /**
   * Finishes a Paste & Apply the way the editor's Apply finishes it. The config
   * is already saved against the render by then, so this renders the picture
   * those pasted settings produce and puts it through the same store step.
   */
  const storePastedEdits = ({ image, editConfig }) =>
    withLoader(async () => {
      const updatedImage = {
        ...image,
        render_image_details: {
          ...image?.render_image_details,
          edit_config: { ...editConfig, edited: false, isImageTouched: true },
        },
      };
      const editedImageUrl = await renderEditedImage(updatedImage);
      await storeEditedImage({ image: updatedImage, editedImageUrl });
    });

  /**
   * Paste & Apply for a product image: save the copied config against the
   * render, then finish it the way the editor's Apply does.
   */
  const pasteAndApplyEdits = async ({ image, copyInfo, updateBulkRenderImageConfig }) => {
    const imageId = image?.id;
    if (!imageId || pastingIdsRef.current.has(imageId)) return;
    pastingIdsRef.current.add(imageId);
    setPastingImages((prev) => [...prev, image]);
    try {
      await applyCopiedEdits({
        copyInfo,
        renderImageIds: image?.render_image,
        updateBulkRenderImageConfig,
        onApplied: (editConfig) => storePastedEdits({ image, editConfig }),
      });
    } finally {
      pastingIdsRef.current.delete(imageId);
      setPastingImages((prev) => prev.filter((pasting) => pasting?.id !== imageId));
    }
  };

  /** Whether a Paste & Apply is in flight for that image, for its card badge. */
  const isPastingImage = (imageId) => pastingImages.some((image) => image?.id === imageId);

  return {
    editedImages,
    editImageLoading: pendingCount > 0,
    ensureEditedImage,
    storeEditedImage,
    pasteAndApplyEdits,
    isPastingImage,
  };
};

export default useProductImageEdits;
